/**
 * Orik's components in the harness conversation panel's seats.
 *
 * Each registration waits for its seat's declaration (`slots.inject`), because the seats belong to
 * `ui-conversation` and `ui-tool`, which the kernel applies in its own order. A single seat's default
 * occupant is shadowed by registering at a lower priority (the lowest renders), so a default comes back by
 * itself if Orik's registration is ever withdrawn.
 *
 * Everything else in the panel is the harness's own: its header, its model picker, and its generic card for
 * every tool without a view here.
 */
import type { ComponentType } from "react";
import { OrikDictationControl, OrikHeroMark, OrikStarters } from "../src/components/harness-seats";
import { presentView, type ToolViewPrimitives } from "../src/components/harness-tool-views";
import type { HarnessContext } from "./orik-harness";

/** The `ui-primitives` components Orik's occupants draw with, as the module loader resolves them. */
export type Primitives = ToolViewPrimitives;

/** The rank Orik's occupants take over a seat's default: below the harness's own (0). */
export const ORIK_PRIORITY = -10;

/** Register one occupant once its seat is declared, for the lifetime of the kernel. */
function occupy(ctx: HarnessContext, seat: string, options: Record<string, unknown>, component: ComponentType<never>): void {
  ctx.slots.inject(seat, () => ctx.slots.register({ name: seat, ...options }, component));
}

/**
 * Register every Orik occupant.
 *
 * @param openFile - opens one of a session's files in the right sidebar.
 */
export function installOrikSeats(ctx: HarnessContext, primitives: Primitives, openFile: (sessionId: string, path: string) => void): void {
  // Voice input (Gemini Live), where the harness puts its own experimental voice plugin.
  occupy(ctx, "conversation.input.activity", { priority: ORIK_PRIORITY }, OrikDictationControl as ComponentType<never>);
  // The blank conversation's hero: Orik's mark beside the headline (patched to Orik's copy, see
  // `patches/@deepseek-ai+dsh-client-ui-conversation+*.patch`), and starter prompts above the composer — the
  // input dock is the hero's one seat beside the composer card.
  occupy(ctx, "conversation.hero.brand.mark", { priority: ORIK_PRIORITY }, OrikHeroMark as ComponentType<never>);
  occupy(ctx, "conversation.input.dock", { id: "orik-starters", order: 100 }, OrikStarters as ComponentType<never>);
  // Presented files open in the right sidebar, and download from Orik's durable copy.
  occupy(ctx, "tool.call.toolview", { key: "present", priority: ORIK_PRIORITY }, presentView(primitives, openFile) as ComponentType<never>);
}
